import React, { SFC, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Button,
    Dialog,
    DialogTitle,
    makeStyles,
    DialogContent,
    DialogActions,
    Grid,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    TextField,
    FormControl,
    InputLabel,
    FormHelperText,
    InputAdornment,
} from '@material-ui/core'
import { Form } from 'react-final-form'
// import { TextField as MuiTextField } from 'mui-rff'
import { UserModel } from '../../../shared/types/user'
import { useDispatch } from 'react-redux'
import { editUser } from '../../../redux/actions/users'
import { DialogConfirmButton } from '../../../shared/components/dialogConfirmButton'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import { ChargerConnectorSocketType, getChargerConnectorSocketTypes } from '../../../shared/types/charger'
import { max } from 'lodash'

const useStyles = makeStyles((theme) => ({
    dialog: {
        '& .MuiFilledInput-root': {
            borderRadius: theme.spacing(0.5),
        },
        '& .MuiFilledInput-root::before': {
            border: 0,
        },
    },
    dialogActions: {
        padding: theme.spacing(1, 3, 3, 3),
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflowY: 'hidden',
    },
    addButton: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    removeButton: {
        display: 'inline-flex',
        alignItems: 'center',
        color: theme.palette.error.main,
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        fontWeight: 'bold',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    fullWidth: {
        width: '100%',
    },
}))

interface UserCarEditDialogProps {
    onClose(cars?: UserModel): void
    initialModel?: UserModel
}
const UserCarEditDialog: SFC<UserCarEditDialogProps> = ({ onClose, initialModel }) => {
    const classes = useStyles()
    const { t } = useTranslation()

    const dispatch = useDispatch()

    const [cars, setUserCars] = useState<any[]>(initialModel?.cars || [])
    const [expandedIndex, setExpandedIndex] = useState<number | false>(false)
    const [fieldErrors, setFieldErrors] = useState<Record<number, Record<string, string>>>({})

    const canEditAC = (socketTypes: number[] = []) => socketTypes.some(id => [0, 1].includes(id));
    const canEditDC = (socketTypes: number[] = []) => socketTypes.some(id => [2, 3].includes(id));
    const AC_POWER_OPTIONS = [3.7, 7.4, 11, 17, 22];

    useEffect(() => {
        if (initialModel?.cars && initialModel.cars.length > 0) {
            // const normalizedCars = initialModel.cars.map(car => ({
            //     ...car,
            //     batteryCapacity: (String(car.batteryCapacity) === '' || car.batteryCapacity === 0) ? '11' : car.batteryCapacity, // null -> ''
            //     maxAcPower: (String(car.maxAcPower) === '' || car.maxAcPower === 0) ? '7' : car.maxAcPower, // null -> ''
            //     maxDcPower: (String(car.maxDcPower) === '' || car.maxDcPower === 0) ? '50' : car.maxDcPower // null -> ''
            // }))
            setUserCars(initialModel.cars)
        } else {
            const defaultCar = {
                brand: '',
                model: '',
                carLicensePlate: '',
                year: '',
                socketType: [1],
                batteryCapacity: 60,
                maxAcPower: 11,
                maxDcPower: 0,
                default: true,
            };
            setUserCars([defaultCar]);
            setExpandedIndex(0);
        }
    }, [initialModel])

    const handleAddCar = (form: any) => {
        // const errors = validateCars(cars, t)

        // if (errors) {
        //     console.log('Validation failed:', errors) // Debugging output
        //     return // 🚫 Stop if there are validation errors
        // }

        if (cars.length < 5) {
            const newCar = {
                brand: '',
                model: '',
                carLicensePlate: '',
                year: '',
                socketType: [1],
                batteryCapacity: 60,
                maxAcPower: 11,
                maxDcPower: '',
                default: true,
            }

            const updatedCars = cars.map((car) => ({ ...car, default: false }))
            updatedCars.push(newCar)

            setUserCars(updatedCars)
            form.change('cars', updatedCars)

            setExpandedIndex(updatedCars.length - 1)
        }
    }

    const handleRemoveCar = (index: number, form: any) => {
        if (cars.length > 1) {
            let newCars = cars.filter((_, i) => i !== index);

            // If the removed car was the default, assign default to the last remaining car
            if (cars[index].default) {
                newCars = newCars.map((car, i) => ({ ...car, default: i === newCars.length - 1 }));
            }

            setUserCars(newCars);
            form.change('cars', newCars);

            // If the last car was removed, close the accordion
            setExpandedIndex(false);
        }
    };

    const handleCarChange = (index: number, field: string, value: any) => {
        const updatedCars = [...cars]
        updatedCars[index] = { ...updatedCars[index], [field]: value }
        setUserCars(updatedCars)

        // Remove error for this field when user starts typing
        setFieldErrors((prevErrors) => {
            const newErrors = { ...prevErrors }
            if (newErrors[index]) {
                delete newErrors[index][field]
                if (Object.keys(newErrors[index]).length === 0) {
                    delete newErrors[index]
                }
            }
            return newErrors
        })
    }

    const validateCars = (cars: any[], t: any) => {
        const errors: Record<number, Record<string, string>> = {};
        cars.forEach((car, index) => {
            const carErrors: Record<string, string> = {};
            if (!car.brand?.trim()) carErrors.brand = t('common.required');

            if (Object.keys(carErrors).length > 0) {
                errors[index] = carErrors;
            }
        });
        setFieldErrors(errors);
        return Object.keys(errors).length > 0 ? errors : null;
    };

    // const validateCars = (cars: any[], t: (key: string) => string) => {
    //     const errors: Record<number, Record<string, string>> = {}

    //     cars.forEach((car, index) => {
    //         const carErrors: Record<string, string> = {}

    //         if (!car.brand?.trim()) carErrors.brand = t('common.required')
    //         if (!car.model?.trim()) carErrors.model = t('common.required')
    //         if (!car.carLicensePlate?.trim()) carErrors.carLicensePlate = t('common.required')
    //         if (car.batteryCapacity === '' || car.batteryCapacity === 0) {
    //             carErrors.batteryCapacity = t('common.required')
    //         }
    //         if (car.maxAcPower === '' || car.maxAcPower === 0) {
    //             carErrors.maxAcPower = t('common.required')
    //         }
    //         if (car.maxDcPower === '' || car.maxDcPower === 0) {
    //             carErrors.maxDcPower = t('common.required')
    //         }
    //         if (Object.keys(carErrors).length > 0) {
    //             errors[index] = carErrors
    //         }
    //     })

    //     setFieldErrors(errors)

    //     return Object.keys(errors).length > 0 ? errors : null
    // }

    const socketTypeLabels = getChargerConnectorSocketTypes(t)

    const mapSocketTypes = (types: ChargerConnectorSocketType[]) => {
        return types.map((type) => socketTypeLabels.find((socket) => socket.id === type)?.label || type).join(', ')
    }

    return (
        <Dialog open={true}>
            <DialogTitle>{t('users.manageCars')}</DialogTitle>
            <Form
                onSubmit={async (model) => {
                    const errors = validateCars(cars, t)

                    if (errors) {
                        console.log('Validation failed:', errors) // Debugging output
                        return
                    }

                    const user = {
                        ...model,
                        cars: cars,
                        tags: null,
                        rfids: null,
                    }

                    const result: any = await dispatch(editUser(user as UserModel))
                    if (result) {
                        onClose(result) // Pass the updated user data
                    }
                }}
                initialValues={{ cars, ...initialModel }}
                render={({ handleSubmit, form }) => (
                    <form onSubmit={handleSubmit} noValidate className={classes.form}>
                        <DialogContent>
                            <Grid container alignItems="flex-start" spacing={2}>
                                {cars.map((car, index) => (
                                    <Grid item xs={12} key={index}>
                                        <Accordion
                                            expanded={expandedIndex === index}
                                            onChange={() => setExpandedIndex(expandedIndex === index ? false : index)}
                                        >
                                            <AccordionSummary expandIcon={<ArrowDownwardIcon />}>
                                                <Typography>
                                                    {[car.brand, car.model].filter(Boolean).join(' ') || 'Neznáme vozidlo'}
                                                </Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={6}>
                                                        <TextField
                                                            value={car.brand}
                                                            fullWidth
                                                            required
                                                            inputProps={{
                                                                maxLength: 64,
                                                            }}
                                                            error={!!fieldErrors[index]?.brand}
                                                            helperText={fieldErrors[index]?.brand}
                                                            type="text"
                                                            variant="filled"
                                                            label={t('users.brand')}
                                                            onChange={(e) =>
                                                                handleCarChange(index, 'brand', e.target.value)
                                                            }
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <TextField
                                                            value={car.model}
                                                            fullWidth
                                                            inputProps={{
                                                                maxLength: 64,
                                                            }}
                                                            // error={!!fieldErrors[index]?.model}
                                                            // helperText={fieldErrors[index]?.model}
                                                            type="text"
                                                            variant="filled"
                                                            label={t('users.model')}
                                                            onChange={(e) =>
                                                                handleCarChange(index, 'model', e.target.value)
                                                            }
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <TextField
                                                            value={car.carLicensePlate}
                                                            fullWidth
                                                            inputProps={{
                                                                maxLength: 64,
                                                            }}
                                                            // error={!!fieldErrors[index]?.carLicensePlate}
                                                            // helperText={fieldErrors[index]?.carLicensePlate}
                                                            type="text"
                                                            variant="filled"
                                                            label={t('users.licensePlate')}
                                                            onChange={(e) =>
                                                                handleCarChange(
                                                                    index,
                                                                    'carLicensePlate',
                                                                    e.target.value.toUpperCase()
                                                                )
                                                            }
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <TextField
                                                            value={car.year}
                                                            fullWidth
                                                            inputProps={{
                                                                maxLength: 320,
                                                            }}
                                                            type="text"
                                                            variant="filled"
                                                            label={t('users.year')}
                                                            onChange={(e) =>
                                                                handleCarChange(index, 'year', e.target.value)
                                                            }
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <FormControl variant="filled" className={classes.fullWidth}
                                                            error={!!fieldErrors[index]?.socketType}>
                                                            <InputLabel id={`socketTypeLabel${car.id}`}>
                                                                {t('users.socketType')}
                                                            </InputLabel>
                                                            <Select
                                                                multiple
                                                                fullWidth
                                                                value={car.socketType || []}
                                                                onChange={(e) =>
                                                                    handleCarChange(
                                                                        index,
                                                                        'socketType',
                                                                        e.target.value
                                                                    )
                                                                }
                                                                variant="filled"
                                                                label={t('users.socketType')}
                                                                renderValue={(selected) =>
                                                                    mapSocketTypes(selected as ChargerConnectorSocketType[])
                                                                }
                                                            >
                                                                {socketTypeLabels.map((socketType) => (
                                                                    <MenuItem key={socketType.id} value={socketType.id}>
                                                                        <Checkbox
                                                                            checked={car.socketType?.includes(
                                                                                socketType.id
                                                                            )}
                                                                        />
                                                                        <ListItemText primary={socketType.label} />
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                            {fieldErrors[index]?.socketType && (
                                                                <FormHelperText>{fieldErrors[index]?.socketType}</FormHelperText>
                                                            )}
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <TextField
                                                            value={car.batteryCapacity}
                                                            fullWidth
                                                            inputProps={{
                                                                min: 1,
                                                            }}
                                                            // error={!!fieldErrors[index]?.batteryCapacity}
                                                            // helperText={fieldErrors[index]?.batteryCapacity}
                                                            type="number"
                                                            variant="filled"
                                                            label={t('users.batteryCapacity') + ' (kWh)'}
                                                            onChange={(e) => {
                                                                const val = e.target.value
                                                                handleCarChange(
                                                                    index,
                                                                    'batteryCapacity',
                                                                    val === '' ? '' : Number(val)
                                                                )
                                                            }}
                                                        />
                                                    </Grid>
                                                    {/* <Grid item xs={4}>
                                                        <TextField
                                                            value={car.maxAcPower}
                                                            fullWidth
                                                            inputProps={{
                                                                min: 1,
                                                            }}
                                                            // error={!!fieldErrors[index]?.maxAcPower}
                                                            disabled={!canEditAC(car.socketType)}
                                                            helperText={!canEditAC(car.socketType) ? "Select Type 1/2" : ""}
                                                            type="number"
                                                            InputProps={{ endAdornment: <InputAdornment position="end">kW</InputAdornment> }}
                                                            variant="filled"
                                                            label={t('users.maxAcPower')}
                                                            onChange={(e) => {
                                                                const val = e.target.value
                                                                handleCarChange(
                                                                    index,
                                                                    'maxAcPower',
                                                                    val === '' ? '' : Number(val)
                                                                )
                                                            }}
                                                        />
                                                    </Grid> */}
                                                    <Grid item xs={4}>
                                                        <FormControl variant="filled" fullWidth disabled={!canEditAC(car.socketType)}>
                                                            <InputLabel>{t('users.maxAcPower')} (kW)</InputLabel>
                                                            <Select
                                                                value={car.maxAcPower}
                                                                onChange={(e) => handleCarChange(index, 'maxAcPower', e.target.value)}
                                                            >
                                                                {AC_POWER_OPTIONS.map(pwr => (
                                                                    <MenuItem key={pwr} value={pwr}>{pwr}</MenuItem>
                                                                ))}
                                                            </Select>
                                                            {!canEditAC(car.socketType) && (
                                                                <FormHelperText>{t('users.selectType12')}</FormHelperText>
                                                            )}
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <TextField
                                                            value={car.maxDcPower}
                                                            fullWidth
                                                            inputProps={{
                                                                min: 1,
                                                            }}
                                                            // error={!!fieldErrors[index]?.maxDcPower}
                                                            disabled={!canEditDC(car.socketType)}
                                                            helperText={!canEditDC(car.socketType) ? "Select CCS/CHAdeMO" : ""}
                                                            type="number"
                                                            variant="filled"
                                                            label={t('users.maxDcPower') + ' (kW)'}
                                                            onChange={(e) => {
                                                                const val = e.target.value
                                                                handleCarChange(
                                                                    index,
                                                                    'maxDcPower',
                                                                    val === '' ? '' : Number(val)
                                                                )
                                                            }}
                                                        />
                                                    </Grid>
                                                    {cars.length > 1 && (
                                                        <Grid item xs={12}>
                                                            <Typography
                                                                variant="caption"
                                                                style={{ fontSize: '18px', color: '#000000' }}
                                                            >
                                                                <Checkbox
                                                                    checked={car.default}
                                                                    onChange={() => {
                                                                        const updatedCars = cars.map((c, i) => ({
                                                                            ...c,
                                                                            default: i === index, // Only one car can be default
                                                                        }))
                                                                        setUserCars(updatedCars)
                                                                        form.change('cars', updatedCars)
                                                                    }}
                                                                />
                                                                {t('users.defaultCar')}
                                                            </Typography>
                                                        </Grid>
                                                    )}

                                                    <Grid item xs={12}>
                                                        {cars.length > 1 && (
                                                            <button
                                                                type="button"
                                                                className={classes.removeButton}
                                                                onClick={() => handleRemoveCar(index, form)}
                                                            >
                                                                {t('users.removeCar')}
                                                            </button>
                                                        )}
                                                    </Grid>
                                                </Grid>
                                            </AccordionDetails>
                                        </Accordion>
                                    </Grid>
                                ))}
                            </Grid>
                        </DialogContent>
                        <DialogActions className={classes.dialogActions}>
                            <Button onClick={() => handleAddCar(form)} color="primary" className={classes.addButton}>
                                {t('users.addCar')}
                            </Button>
                            <Button onClick={() => onClose()} color="primary">
                                {t('common.cancel')}
                            </Button>
                            <DialogConfirmButton label={t('common.change')} />
                        </DialogActions>
                    </form>
                )}
            />
        </Dialog>
    )
}

export default UserCarEditDialog
